import { getMainPage } from './page-name';
import { isDefined } from './utils';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

// This validation requires the form of '<name>@<name>.<name>'. This validation is more strict than the HTML email validation.
export const validateEmail = (email: string | undefined) => {
    return (
        email?.match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        ) !== null
    );
};


export const getInitials = (primary: string | undefined, secondary: string | undefined) => {
    if (primary !== undefined && primary.length > 1) {
        return primary
            .match(/(\b\S)?/g)
            ?.join('')
            .toUpperCase()
            .substring(0, 2);
    } else if (secondary !== undefined) {
        return secondary?.toUpperCase().substring(0, 2);
    }
    return undefined;
};

export function getRandomNumber(chars: number) {
    return Math.floor(Math.random() * chars);
};

export function getRandomString(length: number) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars[getRandomNumber(chars.length)];
    }
    return result;
}

// A point's marker label: up to 8 letters, numbers, or hyphens (booth/stall
// codes like "A-3"). Stored as a string so leading zeros like "001" survive.
export function pointNumberValid(number: string | undefined): boolean {
    if (number === undefined) {
        return true;
    }

    if (number.length > 8) {
        return false;
    }

    // Letters, digits, and hyphens — short booth/stall codes like "A-3".
    const shortCodeRegex = /^[a-zA-Z0-9-]*$/;
    if (!shortCodeRegex.test(number)) {
        return false;
    }

    return true;
}

export function isNumberValid(number: string | undefined): boolean {
    if (number === undefined) {
        return true;
    }

    // Check if the string contains only alphanumeric characters or a decimal point
    const alphanumericRegex = /^[0-9.]*$/;
    if (!alphanumericRegex.test(number)) {
        return false;
    }

    return true;
}

export function capitalizeFirstLetter(string: string): string {
    if (!isDefined(string)) {
        return '';
    }
    return string.charAt(0).toUpperCase() + string.slice(1);
}

// SMS-specific validation and formatting functions
export function validateSmsLength(message: string): { isValid: boolean; characterCount: number; segmentCount: number; error?: string } {
    if (!message) {
        return { isValid: true, characterCount: 0, segmentCount: 0 };
    }

    const characterCount = message.length;
    
    // Check if message contains Unicode characters that would require Unicode encoding
    // eslint-disable-next-line no-control-regex
    const hasUnicodeChars = /[^\x00-\x7F]/.test(message);
    const effectiveLimit = hasUnicodeChars ? 70 : 160; // Unicode vs GSM 7-bit
    const segmentLimit = hasUnicodeChars ? 67 : 153; // Unicode vs GSM 7-bit for concatenated messages

    let segmentCount = 1;
    if (characterCount > effectiveLimit) {
        segmentCount = Math.ceil(characterCount / segmentLimit);
    }

    const maxSegments = 3; // Configurable limit
    const isValid = segmentCount <= maxSegments;

    let error: string | undefined;
    if (!isValid) {
        const maxChars = effectiveLimit + (segmentLimit * (maxSegments - 1));
        error = `Message too long. Maximum ${maxChars} characters allowed (${characterCount} used). This would require ${segmentCount} SMS segments, but only ${maxSegments} are allowed.`;
    }

    return {
        isValid,
        characterCount,
        segmentCount,
        error
    };
}

export function formatSmsMessage(template: string, variables: Record<string, string>): string {
    let formatted = template;
    
    // Replace template variables like {{variable_name}}
    Object.entries(variables).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`;
        formatted = formatted.replace(new RegExp(placeholder, 'g'), value || '');
    });

    // Clean up any remaining unreplaced variables
    formatted = formatted.replace(/\{\{[^}]+\}\}/g, '');
    
    // Normalize whitespace
    formatted = formatted.replace(/\s+/g, ' ').trim();
    
    return formatted;
}

// Format a phone number progressively as the user types. Strips non-digits,
// then re-applies common phone formatting so dashes/parens/spaces appear without
// the user needing to type them (which is painful on mobile tel keyboards).
export function formatPhoneNumberAsUserTypes(value: string): string {
    if (!isDefined(value) || value === '') return '';

    const trimmed = value.trim();
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');

    if (digits.length === 0) return hasPlus ? '+' : '';

    if (hasPlus) {
        // International — keep the + and group digits in readable chunks.
        // Country code (1-3 digits) then groups of 3.
        if (digits.length <= 3) return `+${digits}`;
        const cc = digits.slice(0, digits.startsWith('1') ? 1 : digits.length <= 11 ? 2 : 3);
        const rest = digits.slice(cc.length);
        const groups = rest.match(/.{1,3}/g) ?? [];
        return `+${cc} ${groups.join(' ')}`.trim();
    }

    // No leading + — assume US/CA formatting up to 10 digits.
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

    // 11 digits starting with 1 → US with country code.
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    // Anything longer without a + — treat as international and add one.
    const cc = digits.startsWith('1') ? '1' : digits.slice(0, digits.length <= 12 ? 2 : 3);
    const rest = digits.slice(cc.length);
    const groups = rest.match(/.{1,3}/g) ?? [];
    return `+${cc} ${groups.join(' ')}`.trim();
}

export function extractSmsOptInStatus(phoneNumber: string | undefined): boolean {
    // In a real implementation, this would check against a database of opted-in numbers
    // For now, we'll assume explicit opt-in is required for all SMS
    return false; // Always require explicit opt-in
}

export function validatePhoneNumberForSms(phoneNumber: string | undefined): { isValid: boolean; formattedNumber?: string; error?: string } {
    const phoneValidation = validateAndFormatPhoneNumber(phoneNumber);
    
    if (!phoneValidation.formatted) {
        return {
            isValid: false,
            error: phoneValidation.error ?? 'Invalid phone number for SMS'
        };
    }

    // Additional SMS-specific validation
    const formatted = phoneValidation.formatted;
    
    // Check if it's a landline (basic heuristic - could be enhanced with carrier lookup)
    // Most mobile numbers in major countries start with specific prefixes
    const mobilePatterns = [
        /^\+1[2-9]\d{9}$/, // US/Canada mobile (rough pattern)
        /^\+44[7-9]\d{8,9}$/, // UK mobile
        /^\+33[6-7]\d{8}$/, // France mobile
        /^\+49[1-2]\d{8,10}$/, // Germany mobile
        // Add more patterns as needed
    ];

    const isMobile = mobilePatterns.some(pattern => pattern.test(formatted));
    
    if (!isMobile && formatted.startsWith('+1')) {
        // For US numbers, warn about potential landline
        return {
            isValid: true,
            formattedNumber: formatted,
            error: 'Warning: This may be a landline number. SMS delivery is not guaranteed.'
        };
    }

    return {
        isValid: true,
        formattedNumber: formatted
    };
}


export function validateAndFormatPhoneNumber(phoneNumber: string | undefined): { formatted: string | undefined; error: string | undefined } {
    if (!isDefined(phoneNumber) || phoneNumber.trim() === '') {
        return { formatted: undefined, error: 'Phone number is required' };
    }

    // Remove all non-digit characters except leading +
    let cleaned = phoneNumber.trim();
    const hasPlus = cleaned.startsWith('+');

    // Handle international prefixes
    if (cleaned.startsWith('00')) {
        // Convert 00 international prefix to +
        cleaned = '+' + cleaned.substring(2).replace(/\D/g, '');
    } else if (hasPlus) {
        // Keep the + but remove other non-digits
        cleaned = '+' + cleaned.substring(1).replace(/\D/g, '');
    } else {
        // For US numbers without country code
        cleaned = cleaned.replace(/\D/g, '');
        if (cleaned.length === 10) {
            cleaned = '+1' + cleaned;
        } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
            cleaned = '+' + cleaned;
        } else {
            // Cannot determine format
            return {
                formatted: undefined,
                error: 'Invalid phone number format. Please use E.164 format (e.g., +14255550123)'
            };
        }
    }

    // Check if the cleaned number is valid (at least 8 digits after the + sign)
    if (cleaned.length < 9) { // + plus minimum 8 digits
        return {
            formatted: undefined,
            error: 'Phone number is too short. Please include country code and full number'
        };
    }

    // Check if it's a reasonable length (most phone numbers are under 15 digits with country code)
    if (cleaned.length > 16) {
        return {
            formatted: undefined,
            error: 'Phone number is too long. Please check your input'
        };
    }

    // Return the E.164 format: +[country code][number]
    return { formatted: cleaned, error: undefined };
}

/** Two-letter region from the browser locale (e.g. `en-AU` → `AU`), used as the
 *  default country when a number is typed without a `+` code. Falls back to
 *  `US`. Handles script-tagged locales (`zh-Hant-TW` → `TW`). */
export function defaultPhoneRegion(): CountryCode {
    try {
        const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
        for (const locale of locales) {
            let region: string | undefined;
            try {
                region = new Intl.Locale(locale).region ?? undefined;
            } catch {
                region = locale.split('-').filter(Boolean).reverse().find((p) => /^[A-Za-z]{2}$/.test(p));
            }
            if (region && /^[A-Za-z]{2}$/.test(region)) return region.toUpperCase() as CountryCode;
        }
    } catch {
        // navigator/Intl may be unavailable — fall through to the default.
    }
    return 'US';
}

/** Best-effort phone normalization for capture — never throws or rejects, so a
 *  phone entry can't block a registration. Parses the input (local or
 *  international) using the visitor's locale as the default region and returns
 *  E.164 when it's a valid number; otherwise returns the trimmed input as-is so
 *  we still keep whatever they gave us. */
export function normalizePhoneNumber(input: string | undefined, region: CountryCode = defaultPhoneRegion()): string | undefined {
    const trimmed = input?.trim();
    if (!isDefined(trimmed) || trimmed === '') return undefined;
    try {
        const parsed = parsePhoneNumberFromString(trimmed, region);
        if (parsed?.isValid()) return parsed.number;
    } catch {
        // Unparseable input — keep the raw value rather than dropping it.
    }
    return trimmed;
}


export function isUrlValidAndSafe(url: string | undefined, allowEmpty: boolean = false): boolean {
    if (!isDefined(url) || url.trim() === '') {
        return allowEmpty;
    }

    try {
        const validatedUrl = validateUrl(url, { allowEmpty, showToast: true });
        return isDefined(validatedUrl);
    } catch {
        return false;
    }
}

// validate that a url is in a valid format and not malicious
// prevent javascript: urls and other potentially harmful schemes
export function validateUrl(url: string | undefined, options: { allowEmpty?: boolean; showToast?: boolean } = {}): string {
    const { allowEmpty = false, showToast = true } = options;

    const showError = (message: string): never => {
        console.error('URL validation error:', message);
        if (showToast) {
            getMainPage().errorToastElement.showToastAndThrow(message);
        }
        throw new Error(message);
    };

    // Handle empty URLs
    if (!isDefined(url) || url.trim() === '') {
        if (allowEmpty) {
            return '';
        } else {
            showError('URL is required');
        }
    }

    url = url ?? '';

    const trimmedUrl: string = url.trim();

    // Enhanced protocol security checks
    const dangerousProtocols: string[] = [
        'javascript:',
        'data:',
        'vbscript:',
        'file:',
        'ftp:',
        'ftps:',
        'gopher:',
        'ldap:',
        'ldaps:',
        'telnet:',
        'ssh:',
        'news:',
        'nntp:',
        'irc:',
        'prospero:',
        'chrome:',
        'chrome-extension:',
        'moz-extension:',
        'ms-appx:',
        'ms-appx-web:',
        'res:',
        'resource:'
    ];

    const lowerUrl: string = trimmedUrl.toLowerCase();

    // Check for dangerous protocols
    for (const protocol of dangerousProtocols) {
        if (lowerUrl.startsWith(protocol)) {
            showError(`Unsafe protocol detected: ${protocol.replace(':', '')}`);
        }
    }

    // Check for encoded dangerous protocols
    try {
        const decodedUrl: string = decodeURIComponent(lowerUrl);
        for (const protocol of dangerousProtocols) {
            if (decodedUrl.startsWith(protocol)) {
                showError('Potentially unsafe encoded URL detected');
            }
        }
    } catch {
        // If decoding fails, the URL might have malformed encoding, but we'll let the URL constructor validate it
    }

    // Check for suspicious patterns
    const suspiciousPatterns: RegExp[] = [
        // eslint-disable-next-line no-control-regex
        /[\x00-\x1f\x7f-\x9f]/, // Control characters
        /\.\.\//, // Path traversal attempts
        /<script/i, // Script injection attempts
        /<[^>]+\son\w+\s*=/i, // Inline event handler inside an HTML tag
        /expression\s*\(/i, // CSS expression injection
    ];

    for (const pattern of suspiciousPatterns) {
        if (pattern.test(trimmedUrl)) {
            showError('Suspicious URL pattern detected');
        }
    }

    // Validate URL structure using URL constructor
    let parsedUrl: URL = null as unknown as URL;
    try {
        // Handle URLs without protocol
        const urlToTest: string = trimmedUrl.match(/^https?:\/\//)
            ? trimmedUrl
            : `https://${trimmedUrl}`;

        parsedUrl = new URL(urlToTest);
    } catch (error) {
        showError('Invalid URL format');
    }

    // Protocol validation
    const allowedProtocols: string[] = ['http:', 'https:'];
    if (!allowedProtocols.includes(parsedUrl.protocol)) {
        showError(`Only HTTP and HTTPS protocols are allowed`);
    }

    // Hostname validation
    if (!parsedUrl.hostname || parsedUrl.hostname.length === 0) {
        showError('URL must include a valid hostname');
    }

    // Basic hostname validation - must contain at least one dot for domain
    if (!parsedUrl.hostname.includes('.')) {
        showError('URL must include a valid domain (e.g. example.com)');
    }

    // Check for localhost/internal network access
    const internalPatterns: RegExp[] = [
        /^localhost$/i,
        /^127\./,
        /^192\.168\./,
        /^10\./,
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
        /^0\./,
        /^169\.254\./, // Link-local
        /^::1$/, // IPv6 localhost
        /^fe80:/i, // IPv6 link-local
    ];

    for (const pattern of internalPatterns) {
        if (pattern.test(parsedUrl.hostname)) {
            showError('Internal network URLs are not allowed');
        }
    }

    // Additional length validation
    if (trimmedUrl.length > 2048) { // Common browser URL limit
        showError('URL is too long (maximum 2048 characters)');
    }

    return trimmedUrl;
}